import z from 'zod';
import { optionalNumericString } from '../../../lib/schemas/numeric-string';
import type { CheckoutElement, LocaleGroup } from '../types/spark-options';
import { entryValue } from './locale-rows';
import { hasInvalidLocaleValue } from './locale-validation';

type CustomElement = Extract<CheckoutElement, { kind: 'custom' }>;

const nameSchema = z.string().trim().min(1);

/** minlength/maxlength are counts, regardless of field type. */
const countAttrSchema = optionalNumericString({ min: 0, isWhole: true, emptyAsUndefined: true });
const numberAttrSchema = optionalNumericString({ emptyAsUndefined: true });

/** e.g. "10B", "5KB", "30MB" - matches spark-js's parseHumanReadableBytes format. */
const maxSizeSchema = z
  .string()
  .trim()
  .regex(/^[0-9]+(\.[0-9]+)?(K|M|G|T|P)?B$/i)
  .optional();

const dateAttrSchema = z.iso.date().optional();

function isValidPattern(value: string): boolean {
  try {
    new RegExp(value);
    return true;
  } catch {
    return false;
  }
}

const patternAttrSchema = z
  .string()
  .optional()
  .refine((value) => !value || isValidPattern(value));

/**
 * Bare, lowercase extension - no leading dot (e.g. "pdf" not ".pdf" or "PDF"). spark-js compares
 * these against a lowercased, dot-stripped file extension with `===`, so case and the dot matter.
 */
const fileExtensionSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9]+$/);

export function getFileExtensionErrors(fileextensions: string[] | undefined): boolean[] {
  return (fileextensions ?? []).map((ext) => !fileExtensionSchema.safeParse(ext).success);
}

function hasInvalidElementTranslations(element: CustomElement): boolean {
  if (hasInvalidLocaleValue(element.translations)) return true;
  return (element.options ?? []).some(
    (group) =>
      hasInvalidLocaleValue(group.translations) ||
      group.items.some((item) => hasInvalidLocaleValue(item.translations)),
  );
}

/** spark-js only falls back when a locale is absent, so a present locale needs its own text. */
function isTextMissing(translations: LocaleGroup[], key: string): boolean {
  if (translations.length === 0) return true;
  return translations.some(({ entries }) => entryValue(entries, key).trim() === '');
}

/** Only a `select` renders options - blocking on another type would be invisible. */
function hasInvalidDropdownOptions(element: CustomElement): boolean {
  if (element.attributes.type !== 'select') return false;

  const groups = element.options ?? [];
  return (
    groups.length === 0 ||
    groups.some(
      (group) =>
        group.items.length === 0 ||
        group.items.some(
          (item) => item.value.trim() === '' || isTextMissing(item.translations, 'label'),
        ),
    )
  );
}

type ValidatedAttribute =
  | 'minlength'
  | 'maxlength'
  | 'maxsize'
  | 'min'
  | 'max'
  | 'step'
  | 'pattern'
  | 'fileextensions';

/** `translations` and `options` gate publish only - those editors render their own inline errors. */
export type CustomCheckoutElementErrors = Partial<
  Record<'name' | 'translations' | 'title' | 'options' | ValidatedAttribute, true>
>;

/** `min`/`max` are dates for a date field, otherwise plain numbers; `step` is always a number. */
export function getCustomCheckoutElementErrors(
  element: CustomElement,
): CustomCheckoutElementErrors {
  const { name, attributes } = element;
  const errors: CustomCheckoutElementErrors = {};
  const minMaxSchema = attributes.type === 'date' ? dateAttrSchema : numberAttrSchema;

  if (!nameSchema.safeParse(name).success) errors.name = true;
  if (!countAttrSchema.safeParse(attributes.minlength).success) errors.minlength = true;
  if (!countAttrSchema.safeParse(attributes.maxlength).success) errors.maxlength = true;
  if (!maxSizeSchema.safeParse(attributes.maxsize).success) errors.maxsize = true;
  if (!minMaxSchema.safeParse(attributes.min).success) errors.min = true;
  if (!minMaxSchema.safeParse(attributes.max).success) errors.max = true;
  if (!numberAttrSchema.safeParse(attributes.step).success) errors.step = true;
  if (!patternAttrSchema.safeParse(attributes.pattern).success) errors.pattern = true;
  if (getFileExtensionErrors(attributes.fileextensions).some(Boolean)) errors.fileextensions = true;
  if (hasInvalidElementTranslations(element)) errors.translations = true;
  if (isTextMissing(element.translations, 'title')) errors.title = true;
  if (hasInvalidDropdownOptions(element)) errors.options = true;

  return errors;
}

export function hasInvalidCheckoutElements(elements: CheckoutElement[]): boolean {
  return elements.some(
    (element) =>
      element.kind === 'custom' && Object.keys(getCustomCheckoutElementErrors(element)).length > 0,
  );
}
